import { useEffect, useRef, useState } from 'react';
import { useT, LOCALES_META, SUPPORTED_LOCALES, type Locale } from '@/i18n';

type LanguageSwitcherTone = 'default' | 'inverse';

export function LanguageSwitcher({
  compact = false,
  tone = 'default',
}: {
  compact?: boolean;
  tone?: LanguageSwitcherTone;
}) {
  const { locale, setLocale } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const meta = LOCALES_META[locale] ?? LOCALES_META.en;
  const triggerColor = tone === 'inverse' ? '#fff' : 'var(--ink)';
  const triggerBorder = tone === 'inverse' ? '#fff' : 'var(--line-strong)';
  const chevronOpacity = tone === 'inverse' ? 0.82 : 0.5;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }} data-component="language-switcher">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Choose language"
        style={{
          appearance: 'none',
          background: 'transparent',
          border: '1px solid var(--line-strong)',
          borderRadius: 999,
          padding: compact ? '6px 10px' : '7px 12px',
          fontSize: 12.5,
          fontFamily: 'var(--font-mono)',
          letterSpacing: '.04em',
          color: triggerColor,
          borderColor: triggerBorder,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" />
        </svg>
        <span style={{ textTransform: 'uppercase' }}>{meta.flag}</span>
        <span style={{ opacity: chevronOpacity, marginLeft: 2 }}>▾</span>
      </button>
      {open ? (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            insetInlineEnd: 0,
            minWidth: 200,
            background: 'var(--paper)',
            border: '1px solid var(--line-strong)',
            borderRadius: 14,
            boxShadow: '0 20px 60px -20px rgba(14, 27, 51, 0.3)',
            padding: 6,
            zIndex: 100,
          }}
        >
          {SUPPORTED_LOCALES.map((code: Locale) => {
            const m = LOCALES_META[code];
            const selected = code === locale;
            return (
              <button
                key={code}
                role="option"
                aria-selected={selected}
                onClick={() => {
                  void setLocale(code);
                  setOpen(false);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'start',
                  background: selected ? 'var(--paper-2)' : 'transparent',
                  color: 'var(--ink)',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      fontWeight: 600,
                      width: 28,
                      height: 22,
                      borderRadius: 4,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1.5px solid var(--ink)',
                      letterSpacing: '.04em',
                    }}
                  >
                    {m.flag}
                  </span>
                  <span>{m.native}</span>
                </span>
                {selected ? <span style={{ color: 'var(--coral)' }}>✓</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
